import IconCloseBold from 'assets/icons/close-bold.svg';
import LazyMotionWrapper from 'components/lazy-motion-wrapper';
import useMobile from 'hooks/common/use-mobile';
import useOverflowHidden from 'hooks/common/use-overflow-hidden';
import { ComponentPropsWithoutRef, FC, PropsWithChildren } from 'react';
import { Sheet } from 'react-modal-sheet';

type SheetLayer = 'modal' | 'below-modal';

export type SheetComProps = Omit<ComponentPropsWithoutRef<typeof Sheet>, 'children'> & {
  enableUseOverflowHidden?: boolean;
  /** Stacking tier. `below-modal` lets a modal (e.g. the calorie calculator) open on top of the sheet. */
  layer?: SheetLayer;
};

const LAYER_CLASS: Record<SheetLayer, string> = {
  modal: '!z-modal',
  'below-modal': '!z-sheet-below-modal',
};

/**
 * Sheet component based on react-modal-sheet library
 *
 * @see https://github.com/Temzasse/react-modal-sheet
 * In order to avoid an error related the use of motion, we need to wrap the component with LazyMotionWrapper with strict set to false
 */
const SheetComponent: FC<PropsWithChildren<SheetComProps>> = ({
  children,
  enableUseOverflowHidden = true,
  isOpen,
  onClose,
  onOpenStart,
  onCloseStart,
  className,
  layer = 'modal',
  ...others
}) => {
  const isMobile = useMobile();

  useOverflowHidden(isOpen && enableUseOverflowHidden);

  return (
    <LazyMotionWrapper strict={false}>
      <Sheet
        detent="content-height"
        isOpen={isOpen}
        onClose={onClose}
        tweenConfig={{ ease: 'easeOut', duration: 0.3 }}
        disableScrollLocking={true}
        onOpenStart={() => {
          onOpenStart?.();
        }}
        onCloseStart={() => {
          onCloseStart?.();
        }}
        // assign z-index to modal because out of box solution applies z-index:999999
        className={LAYER_CLASS[layer]}
        {...others}
      >
        <Sheet.Container
          className={className}
          style={{
            borderTopLeftRadius: isMobile ? '24px' : '8px',
            borderTopRightRadius: isMobile ? '24px' : '8px',
          }}
        >
          <Sheet.Header />
          <Sheet.Content>
            <Sheet.Scroller>
              {!isMobile && (
                <button
                  className="mt-4 mr-4 ml-auto block"
                  data-test="sheet-close-button"
                  onClick={onClose}
                >
                  <IconCloseBold className="text-text-light-bg-secondary h-4 w-4" />
                </button>
              )}
              {children}
            </Sheet.Scroller>
          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop onTap={onClose} />
      </Sheet>
    </LazyMotionWrapper>
  );
};

export default SheetComponent;
